import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ErrorBoundary } from '@sentry/react'
import dayjs from 'dayjs'
import { useLocation, useParams } from 'wouter'
import queryString from 'query-string'
import ReactCrop from 'react-image-crop'

import { Image } from '@/components/pdf/Image'
import { useCADPageStore } from '@/state'
import { useDocumentState } from '@/state/document'
import {
  DEFAULT_VIEW_HEIGHT,
  DEFAULT_VIEW_WIDTH,
  MIN_VIEW_HEIGHT,
  MIN_VIEW_WIDTH,
} from '@/pages/CADPage/constants'
import {
  ImageTemplate,
  TemplateAttribute,
  useUpdateTemplateElement,
  ViewTemplate,
} from '@/services/hooks/template_attributes'
import { cn } from '@/utils'
import { usePrevious } from '@/utils/hooks'
import { useToast } from '@/components/ui/use-toast'
import { useDocumentPageParams } from '../hooks'
import { useAnnotationLayer } from './Annotations/queries'
import { useAnnotationsContext } from './Annotations/AnnotationsContext'
import {
  getAbsoluteXFromDocumentPageOrder,
  getAbsoluteYFromSubPage,
} from './Annotations/utils'
import { DocumentPage, View } from '@/lib/api/client'
import { BroadcastIcon } from '@/components/icons/BroadcastIcon'
import { ReplaceImageIcon } from '@/components/icons/ReplaceImageIcon'
import { RetakeViewIcon } from '@/components/icons/RetakeViewIcon'
import {
  getViewQuery,
  useListPageViews,
  useUpdateView,
} from '@/services/queries/views'
import { useRelativeToAbsolutePositioning } from '@/utils/hooks/useRelativeToAbsolutePositioning'
import { OptionsContextMenu } from '@/components/core/OptionsContextMenu'
import { useDocumentPageContextQuery } from '../queries'
import { useListImages } from '@/services/queries/images'
import { useKeyboardNudge } from '@/utils/hooks/useKeyboardNudge'
import { useIsTemplateAttributeMultiSelected } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useIsTemplateAttributeMultiSelected'
import { useMultiSelectDraggable } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useMultiSelectDraggable'
import { useMultiSelectHandler } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useMultiSelectHandler'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Button } from '@/components/ui/button'
import { CropIcon, CubeIcon } from '@radix-ui/react-icons'
import { ReplaceImageDialog } from './ReplaceImageDialog'
import { ElementResizeOverlay } from './ElementResizeOverlay'

import 'react-image-crop/dist/ReactCrop.css'
import { Spinner } from '@radix-ui/themes'

export enum ImageTypeEnum {
  Image = 'image',
  View = 'view',
}
type DraggableImageProps = {
  template: TemplateAttribute<ImageTemplate | ViewTemplate>
  isReadOnly?: boolean
  documentPage: DocumentPage
  dataType: ImageTypeEnum
  onNudgeStart?: (showGrid: boolean) => void
  onNudgeEnd?: (hideGrid: boolean) => void
}
export const DraggableImage = ({
  template,
  isReadOnly,
  documentPage,
  dataType,
  onNudgeStart,
  onNudgeEnd,
}: DraggableImageProps) => {
  const INITIAL_DELTA = useMemo(() => ({ x: 0, y: 0 }), [])
  const onLoad = useDocumentState((state) => state.onImageLoaded)
  const setCroppingImageId = useDocumentState(
    (state) => state.setCroppingImageId,
  )
  const croppingImageId = useDocumentState((state) => state.croppingImageId)
  const imgRef = useRef<HTMLImageElement | null>(null)
  const fileUrlRef = useRef<string | null>(null)
  const originalImgRef = useRef<HTMLImageElement | null>(null)
  const clickStartedInsideCropRef = useRef(false)
  const cropRunIdRef = useRef(0)
  const isReplacing = useRef(false)
  const zoom = useDocumentState((state) => state.zoom)
  const zoomRatio = useMemo(() => zoom / 100, [zoom])
  const canCrop = useMemo(
    () => croppingImageId === template.id,
    [croppingImageId, template.id],
  )
  const isMultiSelected = useIsTemplateAttributeMultiSelected({
    templateAttributeId: template.id,
  })
  const imageCrop = useMemo(
    () =>
      template?.template_values?.imageCrop || {
        unit: '%',
        x: 0,
        y: 0,
        width: 100,
        height: 100,
      },
    [template?.template_values?.imageCrop],
  )
  const [temporalImageCrop, setTemporalImageCrop] = useState(null)
  const isAbsolute = useMemo(
    () => template.template_values.imagePosition === 'absolute',
    [template.template_values.imagePosition],
  )
  const imageId = useMemo(
    () => template?.template_values.imageId || template?.template_values.viewId,
    [template],
  )
  const prevImageId = usePrevious(imageId)
  const documentPageId = useMemo(
    () => documentPage.id as string,
    [documentPage],
  )
  const selectElement = useDocumentState((state) => state.setSelectedElement)
  const selectedElement = useDocumentState((state) => state.selectedElement)
  const isSelected = useMemo(
    () =>
      selectedElement?.documentPageId === documentPageId &&
      selectedElement?.id === template?.id,
    [selectedElement, documentPageId, template?.id],
  )
  const {
    bringForward,
    bringToFront,
    sendBackward,
    sendToBack,
    isLoading: isOptionsMenuLoading,
    maxZIndex,
  } = useAnnotationLayer(documentPageId)
  const { data } = useDocumentPageContextQuery()
  const { data: views } = useListPageViews({
    cadVersionId: data?.version?.id,
    documentVersionId: documentPage.document_version,
    enabled: true, // Always load both to support replace functionality
  })
  const { data: images } = useListImages({
    documentVersionId: documentPage.document_version,
    enabled: true, // Always load both to support replace functionality
  })
  const { projectId, documentId } = useParams<{
    projectId: string
    documentId: string
  }>()

  const imageData = useMemo(() => {
    if (dataType === ImageTypeEnum.Image) {
      return images?.find((image) => image.id === imageId)
    }
    return views?.find((view) => view.id === imageId)
  }, [images, views, dataType, imageId])

  const userFacingDownloadUrl = useMemo(
    () => `${imageData?.name}.jpg`,
    [imageData],
  )
  const { downloadUrl, isPlaceholder } = useMemo(() => {
    const baseUrl = window.location.origin

    const placeholderImage =
      dataType === ImageTypeEnum.Image
        ? `${baseUrl}/static/images/upload-image-placeholder.png`
        : `${baseUrl}/static/images/cad-placeholder.png`

    return imageData?.download_url
      ? { downloadUrl: imageData.download_url, isPlaceholder: false }
      : { downloadUrl: placeholderImage, isPlaceholder: true }
  }, [imageData, dataType])

  const [croppedImageUrl, setCroppedImageUrl] = useState<string>(downloadUrl)
  const [hasCroppedChanged, setHasCroppedChanged] = useState(false)
  const [isLoading, setIsLoading] = useState(
    !!template?.template_values?.imageCrop,
  )
  const [hasImageLoaded, setHasImageLoaded] = useState(false)
  const [isReplaceDialogOpen, setIsReplaceDialogOpen] = useState(false)
  const [isReplacingImage, setIsReplacingImage] = useState(false)
  const imageContainerRef = useRef<HTMLDivElement>(null)
  const [toolbarPos, setToolbarPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  })

  const updateToolbarPosition = useCallback(() => {
    if (!imageContainerRef.current) return
    const rect = imageContainerRef.current.getBoundingClientRect()
    const newPos = {
      top: rect.top + window.scrollY - 40,
      left: rect.left + window.scrollX + rect.width / 2,
    }

    // Only update state if position has changed (avoid infinite loops)
    setToolbarPos((prev) => {
      if (
        Math.abs(prev.top - newPos.top) < 0.5 &&
        Math.abs(prev.left - newPos.left) < 0.5
      ) {
        return prev // Return same reference to prevent re-render
      }
      return newPos
    })
  }, [])

  useEffect(() => {
    if (!isSelected || canCrop || isReplacingImage || isReplaceDialogOpen)
      return
    updateToolbarPosition()

    const onScroll = () => updateToolbarPosition()
    const onResize = () => updateToolbarPosition()

    let raf = 0
    const tick = () => {
      updateToolbarPosition()
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
    }
  }, [
    isSelected,
    canCrop,
    isReplacingImage,
    isReplaceDialogOpen,
    updateToolbarPosition,
  ])

  useEffect(() => {
    if (!downloadUrl || isPlaceholder) return

    cropRunIdRef.current += 1
    setHasImageLoaded(false)

    const img = new window.Image()
    img.crossOrigin = 'anonymous'
    img.src = downloadUrl

    const handleLoad = () => {
      originalImgRef.current = img
      setHasImageLoaded(true)
    }

    if (img.complete) {
      handleLoad()
    } else {
      img.onload = handleLoad
    }

    return () => {
      cropRunIdRef.current += 1
      setHasImageLoaded(false)
      img.onload = null
    }
  }, [downloadUrl, isPlaceholder])

  useEffect(() => {
    if (!isPlaceholder) {
      setCroppedImageUrl(downloadUrl)
    }
  }, [isPlaceholder, downloadUrl])

  useEffect(() => {
    if (!hasImageLoaded) return
    void makeClientCrop(imageCrop)
  }, [imageCrop, hasImageLoaded, downloadUrl])

  const timestamp = useMemo(() => {
    if (dataType === ImageTypeEnum.View) {
      return (imageData as View)?.created
    }
  }, [imageData])
  const isDynamic = useMemo(() => (imageData as View)?.dynamic, [imageData])
  const documentPageOrder = useMemo(
    () => (imageData as View)?.document_page_order,
    [imageData],
  )
  const isAddingElement = useDocumentState((state) => state.isAddingElement)
  const { setIsResizing } = useAnnotationsContext()
  const [{ position, delta }, setPosition] = useState({
    position: {
      x: 0,
      y: 0,
    },
    delta: INITIAL_DELTA,
  })
  const [hasLoadedInitialPosition, setHasLoadedInitialPosition] =
    useState(false)
  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  })
  const positionData = useMemo(
    () => template?.template_values?.position,
    [template],
  )
  const imageSize = useMemo(() => template?.template_values?.size, [template])
  const subPageId = useMemo(
    () => template?.template_values?.subPageId || documentPageId,
    [template],
  )
  const documentSubPages = useMemo(
    () =>
      (documentPage?.template_values?.subPages as Array<string>) ?? [
        documentPage?.id,
      ],
    [documentPage],
  )
  const [, setLocation] = useLocation()
  const { toast } = useToast()
  const { cv: cadVersionId } = useDocumentPageParams()
  const { mutateAsync: updateView } = useUpdateView()
  const setCadPageState = useCADPageStore((state) => state.setState)
  const onDownloadHandler = useCallback(async () => {
    try {
      const response = await fetch(downloadUrl)

      if (!response.ok) {
        throw new Error('Network response was not ok')
      }

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = userFacingDownloadUrl

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Uh oh! Something went wrong.',
        description: `There was a problem downloading the image: ${error}`,
      })
    }
  }, [downloadUrl, userFacingDownloadUrl])

  const cadLink = useMemo(() => {
    return `/p/${projectId}/document/${documentId}/cad?viewId=${imageId}${cadVersionId ? `&cv=${cadVersionId}` : ''}`
  }, [imageId])

  const onOpenInCadHandler = useCallback(() => {
    setLocation(`~${cadLink}`)
  }, [cadLink])

  const onCopyUrlHandler = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(
        `${document.location.origin}${cadLink}`,
      )
      toast({
        variant: 'success',
        description: 'Image URL copied to clipboard',
      })
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Uh oh! Something went wrong.',
        description: `There was a problem generating the image URL: ${error}`,
      })
    }
  }, [cadLink])

  const onMakeStatic = useCallback(async () => {
    if (!imageId) return
    const view = await getViewQuery({ viewId: imageId })
    await updateView({
      viewId: imageId as string,
      values: {
        ...view,
        document_page_id: view.document_page ?? null,
        dynamic: false,
      },
    })
  }, [imageId, updateView])

  const retakeLocation = useMemo(() => {
    const qs = queryString.stringify({
      cv: cadVersionId,
      page_id: `${documentPageId}`,
      subpage_id: `${subPageId}`,
    })
    return `~/p/${projectId}/document/${documentId}/edit?${qs}`
  }, [cadVersionId, documentPageId, projectId, documentId, subPageId])

  const { mutateAsync: updateTemplate } = useUpdateTemplateElement({
    documentPageId,
  })

  const { attributes, isDragging, listeners, transform, setNodeRef } =
    useMultiSelectDraggable({
      disabled: isAddingElement || isReadOnly || !!croppingImageId,
      id: template?.id as string,
      data: { type: dataType },
    })

  const { bind, previewDelta } = useKeyboardNudge({
    isActive: Boolean(isSelected) && Boolean(selectedElement?.isRemovable),
    showGridOnNudge: true,
    onNudgeStart,
    onNudgeEnd,
    template,
    documentPage,
  })

  const updateImageDimensionsHandler = useCallback(
    async (
      size: { width: number; height: number },
      position: { x: number; y: number },
    ) => {
      await updateTemplate({
        payload: {
          size,
          position: { ...position, z: positionData?.z },
        },
        data_type: dataType,
        templateAttributeId: template?.id as string,
      })
    },
    [dataType, template?.id, positionData, updateTemplate],
  )

  const { registerSelectableElementRef } = useRelativeToAbsolutePositioning()

  const parentRefCallback = useCallback(
    (el: HTMLDivElement | null) => {
      if (template.id) {
        registerSelectableElementRef(template.id, el)
      }
    },
    [template.id, registerSelectableElementRef],
  )

  useEffect(() => {
    if (!imageSize) return
    setContainerDimensions(imageSize)
  }, [imageSize])

  // Reset image when imageId changes (for replace functionality)
  useEffect(() => {
    if (!imageId || imageId === prevImageId) return
    setCroppedImageUrl(downloadUrl)

    if (!imageSize) {
      setContainerDimensions({
        width: 0,
        height: 0,
      })
    }
    setHasImageLoaded(false)
  }, [imageId, prevImageId, downloadUrl, imageSize])

  useEffect(() => {
    const tX = transform?.x ?? 0
    const tY = transform?.y ?? 0
    const pX = previewDelta?.x ?? 0
    const pY = previewDelta?.y ?? 0

    // If everything is 0, this is typically the "drag ended" / "idle" state.
    // Don't overwrite delta here — let it be reset only when positionData changes.
    if (tX === 0 && tY === 0 && pX === 0 && pY === 0) {
      return
    }

    setPosition((prevPosition) => ({
      ...prevPosition,
      delta: {
        x: (tX + pX) / zoomRatio,
        y: (tY + pY) / zoomRatio,
      },
    }))
  }, [transform, previewDelta, zoomRatio])

  useEffect(() => {
    if (!positionData) return
    setPosition({
      position: positionData,
      delta: INITIAL_DELTA,
    })
  }, [INITIAL_DELTA, positionData])

  useEffect(() => {
    if (template?.template_values?.position === position) {
      setHasLoadedInitialPosition(true)
    }
  }, [template, position])

  const imageOptions = useMemo(() => {
    const options: Array<{
      label: string
      action: () => void
    }> = []

    options.push({
      label: 'Replace',
      action: () => setIsReplaceDialogOpen(true),
    })

    if (!isPlaceholder) {
      options.push({
        label: 'Download',
        action: onDownloadHandler,
      })
    }

    if (dataType === ImageTypeEnum.View && isDynamic) {
      if (isDynamic) {
        options.push(
          {
            label: 'Make static',
            action: onMakeStatic,
          },
          {
            label: 'Open in CAD',
            action: onOpenInCadHandler,
          },
          {
            label: 'Re-take',
            action: () => {
              if (!imageId) return
              setCadPageState({
                retakeLocation,
                retakeViewId: imageId,
                isRetakeModeActive: true,
              })
              selectElement({
                documentPageId,
                id: template?.id as string,
                isRemovable: true,
                type: dataType,
              })
              onOpenInCadHandler()
            },
          },
          {
            label: 'Copy URL',
            action: onCopyUrlHandler,
          },
        )
      }
    }

    return options
  }, [
    dataType,
    isDynamic,
    isPlaceholder,
    onDownloadHandler,
    onMakeStatic,
    onOpenInCadHandler,
    onCopyUrlHandler,
    imageId,
    setCadPageState,
    retakeLocation,
    selectElement,
    documentPageId,
    template?.id,
    setIsReplaceDialogOpen,
  ])

  const onImageLoad = useCallback(
    (e) => {
      if (isPlaceholder) return
      e.stopPropagation()
      imgRef.current = e.currentTarget

      const setLoaded = () => {
        if (imgRef.current) {
          // Only call onLoad if image wasn't already marked as loaded
          if (!imgRef.current.hasAttribute('data-loaded')) {
            imgRef.current.setAttribute('data-loaded', 'true')
            onLoad()
          }
        }
      }

      const aspect =
        e.currentTarget.naturalWidth / e.currentTarget.naturalHeight

      const height = Math.min(e.currentTarget.height, DEFAULT_VIEW_HEIGHT)
      const width = height * aspect

      setLoaded()

      setContainerDimensions((prev) => {
        if (!prev.width || !prev.height) {
          if (
            Math.abs(prev.width - width) < 0.5 &&
            Math.abs(prev.height - height) < 0.5
          ) {
            return prev
          }
          return {
            height,
            width,
          }
        } else if (isReplacing.current) {
          isReplacing.current = false
          const newHeight = prev.height ? prev.height : height

          // Schedule dimension update after state settles
          setTimeout(
            () =>
              void updateImageDimensionsHandler(
                {
                  width: newHeight * aspect,
                  height: newHeight,
                },
                {
                  x: position.x,
                  y: position.y,
                },
              ),
            100,
          )

          return {
            height: newHeight,
            width: newHeight * aspect,
          }
        }
        return prev
      })
    },
    [isPlaceholder, updateImageDimensionsHandler, position, onLoad],
  )

  useEffect(() => {
    const escFunction = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setHasCroppedChanged(false)
        setCroppingImageId(null)
      }
    }

    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      clickStartedInsideCropRef.current = Boolean(target.closest('.react-crop'))
    }

    const saveCropAndExit = async (event: MouseEvent) => {
      event.stopPropagation()
      if (clickStartedInsideCropRef.current) {
        clickStartedInsideCropRef.current = false
        return
      }

      const target = event.target as HTMLElement
      if (target.closest('.react-crop')) return

      if (hasCroppedChanged) {
        await updateTemplate({
          payload: {
            ...template?.template_values,
            imageCrop: temporalImageCrop,
          },
          data_type: dataType,
          templateAttributeId: template?.id as string,
        })

        setHasCroppedChanged(false)
        setCroppingImageId(null)
      }
    }

    if (canCrop) {
      document.addEventListener('keydown', escFunction, false)
      document.addEventListener('mousedown', handleMouseDown, true)
      document.addEventListener('click', saveCropAndExit, false)
    }

    return () => {
      document.removeEventListener('keydown', escFunction, false)
      document.removeEventListener('mousedown', handleMouseDown, true)
      document.removeEventListener('click', saveCropAndExit, false)
    }
  }, [canCrop, hasCroppedChanged, temporalImageCrop, clickStartedInsideCropRef])

  const onCropChange = (_, percentCrop) => {
    clickStartedInsideCropRef.current = true
    setHasCroppedChanged(true)
    setTemporalImageCrop(percentCrop)
  }

  const makeClientCrop = async (percentCrop) => {
    const image = originalImgRef.current
    if (image && percentCrop.width && percentCrop.height) {
      const runId = ++cropRunIdRef.current
      setIsLoading(true)

      try {
        const nextUrl = await getCroppedImg(image, percentCrop)

        if (runId !== cropRunIdRef.current) {
          window.URL.revokeObjectURL(nextUrl)
          return
        }

        if (fileUrlRef.current) {
          window.URL.revokeObjectURL(fileUrlRef.current)
        }
        fileUrlRef.current = nextUrl

        setCroppedImageUrl(nextUrl)
      } finally {
        if (runId === cropRunIdRef.current) {
          setIsLoading(false)
        }
      }
    }
  }

  const getCroppedImg = (image, percentCrop) => {
    const canvas = document.createElement('canvas')
    const cropX = (percentCrop.x / 100) * image.naturalWidth
    const cropY = (percentCrop.y / 100) * image.naturalHeight
    const cropWidth = (percentCrop.width / 100) * image.naturalWidth
    const cropHeight = (percentCrop.height / 100) * image.naturalHeight

    canvas.width = cropWidth
    canvas.height = cropHeight

    const ctx = canvas.getContext('2d')

    ctx?.drawImage(
      image,
      cropX,
      cropY,
      cropWidth,
      cropHeight,
      0,
      0,
      cropWidth,
      cropHeight,
    )

    return new Promise<string>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Canvas is empty'))
          return
        }
        const url = window.URL.createObjectURL(blob)
        resolve(url)
      }, 'image/png')
    })
  }

  useEffect(() => {
    return () => {
      if (fileUrlRef.current) {
        window.URL.revokeObjectURL(fileUrlRef.current)
        fileUrlRef.current = null
      }
    }
  }, [])

  const {
    onPointerDown: multiSelectKeydownHandler,
    onPointerUp: multiSelectKeyupHandler,
  } = useMultiSelectHandler()

  const optionGroups = useMemo(() => {
    const groups = [
      {
        options: [
          {
            label: 'Bring to front',
            action: () => bringToFront(template?.id as string),
          },
          {
            label: 'Bring forward',
            action: () => bringForward(template?.id as string),
          },
          {
            label: 'Send backward',
            action: () => sendBackward(template?.id as string),
          },
          {
            label: 'Send to back',
            action: () => sendToBack(template?.id as string),
          },
        ],
      },
      {
        options: imageOptions,
      },
    ]

    if (!isPlaceholder) {
      groups.push({
        options: [
          {
            label: 'Crop',
            action: () => setCroppingImageId(template.id as string),
          },
        ],
      })
    }

    return groups
  }, [
    bringForward,
    bringToFront,
    sendBackward,
    sendToBack,
    template?.id,
    imageOptions,
    isPlaceholder,
    setCroppingImageId,
  ])

  if (
    template?.template_values?.position !== position &&
    !hasLoadedInitialPosition
  ) {
    return <></>
  }

  return (
    <OptionsContextMenu
      isReadonly={isReadOnly}
      isDisabled={isOptionsMenuLoading || canCrop}
      onAuxClick={() => {
        selectElement({
          documentPageId,
          id: template?.id as string,
          isRemovable: true,
          type: dataType,
        })
      }}
      optionsGroups={optionGroups}
      zIndex={maxZIndex + 3}
    >
      <div
        {...bind}
        {...attributes}
        {...listeners}
        className={cn('absolute !box-content', {
          'cursor-move': !isReadOnly,
          'cursor-default': isReadOnly,
          'shadow-md': isDragging,
          'shadow-gray-200': isDragging,
          'bg-sky-200/10': isDragging,
          'z-[100]': isDragging,
        })}
        style={{
          translate: `${delta?.x || 0}px ${delta?.y || 0}px`,
          top:
            getAbsoluteYFromSubPage({
              subPageId,
              relativeY: position?.y || 0,
              subPageIds: documentSubPages,
              isRelative: !isAbsolute,
              relativeOffset: 132,
            }) +
            (canCrop ? 0 : containerDimensions.height * (imageCrop.y / 100)),
          left:
            getAbsoluteXFromDocumentPageOrder({
              relativeX: position?.x || 0,
              isRelative: !isAbsolute,
              documentPageOrder,
              relativeOffset: 350,
            }) +
            (canCrop ? 0 : containerDimensions.width * (imageCrop.x / 100)),
          zIndex: canCrop ? maxZIndex + 1 : (positionData?.z ?? 1000),
          pointerEvents: 'all',
        }}
        ref={parentRefCallback}
        onClick={(event) => {
          event.stopPropagation()
          selectElement({
            documentPageId,
            id: template?.id as string,
            isRemovable: true,
            type: dataType,
          })
        }}
      >
        <div className="relative">
          {isSelected &&
            !canCrop &&
            !isReplacingImage &&
            !isReplaceDialogOpen &&
            createPortal(
              <div
                className="fixed flex justify-center"
                style={{
                  top: `${toolbarPos.top}px`,
                  left: `${toolbarPos.left}px`,
                  transform: 'translateX(-50%)',
                  zIndex: maxZIndex + 2,
                  pointerEvents: 'auto',
                }}
              >
                <div
                  className="flex items-center gap-1 bg-white border border-gray-200 rounded-md shadow-md p-0.5"
                  style={{ pointerEvents: 'all' }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <TooltipProvider>
                    {/* Replace */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          onClick={(e) => {
                            e.stopPropagation()
                            setIsReplaceDialogOpen(true)
                          }}
                        >
                          <ReplaceImageIcon className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-black text-white">
                        <p>Replace</p>
                      </TooltipContent>
                    </Tooltip>

                    {/* Retake */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          disabled={
                            isPlaceholder ||
                            dataType !== ImageTypeEnum.View ||
                            !isDynamic
                          }
                          onClick={(e) => {
                            e.stopPropagation()
                            if (!imageId) return
                            setCadPageState({
                              retakeLocation,
                              retakeViewId: imageId,
                              isRetakeModeActive: true,
                            })
                            selectElement({
                              documentPageId,
                              id: template?.id as string,
                              isRemovable: true,
                              type: dataType,
                            })
                            onOpenInCadHandler()
                          }}
                        >
                          <RetakeViewIcon className="h-4 w-4 stroke-current" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-black text-white">
                        <p>Retake</p>
                      </TooltipContent>
                    </Tooltip>

                    {/* Go to CAD */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          disabled={dataType !== ImageTypeEnum.View}
                          onClick={(e) => {
                            e.stopPropagation()
                            onOpenInCadHandler()
                          }}
                        >
                          <CubeIcon className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-black text-white">
                        <p>Go to CAD</p>
                      </TooltipContent>
                    </Tooltip>

                    {/* Crop */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0"
                          disabled={isPlaceholder}
                          onClick={(e) => {
                            e.stopPropagation()
                            setCroppingImageId(template.id as string)
                          }}
                        >
                          <CropIcon className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-black text-white">
                        <p>Crop</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>,
              document.body,
            )}

          {/* Replace Dialog */}
          <ReplaceImageDialog
            isOpen={isReplaceDialogOpen}
            onOpenChange={setIsReplaceDialogOpen}
            template={template}
            documentPageId={documentPageId}
            documentVersionId={documentPage.document_version}
            documentId={documentId}
            cadVersionId={cadVersionId}
            images={images || []}
            isReadOnly={!!isReadOnly}
            onReplaceStart={() => {
              isReplacing.current = true
              setIsReplacingImage(true)
            }}
            onReplaceEnd={() => setIsReplacingImage(false)}
            zIndex={maxZIndex + 3}
          />

          <div
            ref={imageContainerRef}
            style={{
              width:
                containerDimensions.width > 0 && containerDimensions.height > 0
                  ? canCrop
                    ? containerDimensions.width
                    : (containerDimensions.width * imageCrop.width) / 100
                  : DEFAULT_VIEW_WIDTH,
              height:
                containerDimensions.width > 0 && containerDimensions.height > 0
                  ? canCrop
                    ? containerDimensions.height
                    : (containerDimensions.height * imageCrop.height) / 100
                  : DEFAULT_VIEW_HEIGHT,
            }}
          >
            {isLoading || isReplacingImage ? (
              <Spinner className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50" />
            ) : (
              isDynamic &&
              isSelected && (
                <BroadcastIcon
                  color="#238636"
                  className="absolute top-2 left-2 z-50 opacity-50"
                />
              )
            )}
            <div
              ref={setNodeRef}
              className={cn(
                'flex flex-1 flex-col justify-center items-center h-full w-full',
              )}
            >
              {canCrop ? (
                <ReactCrop
                  crop={temporalImageCrop || (imageCrop as any)}
                  className="react-crop"
                  onChange={onCropChange}
                >
                  <Image
                    className="min-h-full h-auto w-auto"
                    src={downloadUrl}
                  />
                </ReactCrop>
              ) : (
                <>
                  <ErrorBoundary fallback={<div>Failed to load image</div>}>
                    <div
                      className={cn(
                        'h-full w-full cursor-move flex justify-center flex-col items-center relative',
                        {
                          'cursor-move': !isReadOnly,
                          'cursor-default': isReadOnly,
                        },
                      )}
                    >
                      <div
                        className="absolute"
                        style={{
                          height:
                            containerDimensions.height *
                              (imageCrop.height / 100) || '100%',
                          width:
                            containerDimensions.width *
                              (imageCrop.width / 100) || '100%',
                          overflow: 'hidden',
                        }}
                      >
                        <Image
                          src={croppedImageUrl}
                          className={cn('min-h-full h-auto w-auto', {
                            'cursor-move': !isReadOnly,
                            'cursor-default': isReadOnly,
                            visible: !isLoading,
                            invisible: isLoading,
                          })}
                          {...(isPlaceholder && {
                            'data-placeholder': true,
                          })}
                          data-testid="draggable-image"
                          onLoad={onImageLoad}
                          onPointerDown={(e) => {
                            if (template) {
                              multiSelectKeydownHandler(e as any, template)
                            }
                          }}
                          onPointerUp={(e) => {
                            multiSelectKeyupHandler(e)
                          }}
                        />
                      </div>
                    </div>
                  </ErrorBoundary>
                  {isSelected && timestamp && (
                    <div className="text-xs m-0 absolute bottom-0 font-medium text-gray-500">
                      {dayjs(timestamp).format('MMM DD, YYYY hh:mm A')}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Portal-based resize handlers that render on top */}
          <ElementResizeOverlay
            isVisible={
              !isReadOnly &&
              (isSelected || isMultiSelected) &&
              !canCrop &&
              !isDragging
            }
            containerRef={imageContainerRef}
            maxZIndex={maxZIndex}
            currentBoundingWidth={
              canCrop
                ? containerDimensions.width
                : (containerDimensions.width * imageCrop.width) / 100
            }
            currentBoundingHeight={
              canCrop
                ? containerDimensions.height
                : (containerDimensions.height * imageCrop.height) / 100
            }
            zoom={zoom}
            showOutline={isSelected || isMultiSelected}
            lockAspectRatio={true}
            minWidth={MIN_VIEW_WIDTH}
            minHeight={MIN_VIEW_HEIGHT}
            enabledHandlers={{
              topLeft: true,
              topRight: true,
              bottomLeft: true,
              bottomRight: true,
            }}
            onResizeStart={() => {
              if (setIsResizing) setIsResizing(true)
            }}
            onResize={(newBoundingWidth, newBoundingHeight, direction) => {
              // This is the bounding width/height (what the user sees after crop is applied)
              const cropWidthFactor =
                imageCrop.width && imageCrop.width > 0
                  ? imageCrop.width / 100
                  : 1
              const cropHeightFactor =
                imageCrop.height && imageCrop.height > 0
                  ? imageCrop.height / 100
                  : 1

              const prevBaseWidth = containerDimensions.width
              const prevBaseHeight = containerDimensions.height

              const prevBoundingWidth = prevBaseWidth * cropWidthFactor
              const prevBoundingHeight = prevBaseHeight * cropHeightFactor

              const widthDiff = newBoundingWidth - prevBoundingWidth
              const heightDiff = newBoundingHeight - prevBoundingHeight

              // Calculate the actual base dimensions (before crop)
              const newBaseWidth = newBoundingWidth / cropWidthFactor
              const newBaseHeight = newBoundingHeight / cropHeightFactor

              const cropXFactor = imageCrop?.x ? imageCrop.x / 100 : 0
              const cropYFactor = imageCrop?.y ? imageCrop.y / 100 : 0

              const cropOffsetDeltaX = canCrop
                ? 0
                : (newBaseWidth - prevBaseWidth) * cropXFactor
              const cropOffsetDeltaY = canCrop
                ? 0
                : (newBaseHeight - prevBaseHeight) * cropYFactor

              const deltaUpdates = {
                topLeft: { x: -widthDiff, y: -heightDiff },
                bottomLeft: { x: -widthDiff, y: 0 },
                topRight: { x: 0, y: -heightDiff },
                bottomRight: { x: 0, y: 0 },
                top: { x: 0, y: -heightDiff },
                bottom: { x: 0, y: 0 },
                left: { x: -widthDiff, y: 0 },
                right: { x: 0, y: 0 },
              }

              setContainerDimensions((prev) => {
                // Only update if dimensions have actually changed
                if (
                  Math.abs(prev.width - newBaseWidth) < 0.5 &&
                  Math.abs(prev.height - newBaseHeight) < 0.5
                ) {
                  return prev
                }
                return {
                  width: newBaseWidth,
                  height: newBaseHeight,
                }
              })

              setPosition((prevPosition) => ({
                ...prevPosition,
                delta: {
                  x:
                    prevPosition.delta.x +
                    (deltaUpdates[direction]?.x || 0) -
                    cropOffsetDeltaX,
                  y:
                    prevPosition.delta.y +
                    (deltaUpdates[direction]?.y || 0) -
                    cropOffsetDeltaY,
                },
              }))
            }}
            onResizeStop={(finalBoundingWidth, finalBoundingHeight) => {
              if (setIsResizing) setIsResizing(false)

              // Convert bounding dimensions back to base dimensions
              const cropWidthFactor =
                imageCrop.width && imageCrop.width > 0
                  ? imageCrop.width / 100
                  : 1
              const cropHeightFactor =
                imageCrop.height && imageCrop.height > 0
                  ? imageCrop.height / 100
                  : 1

              const finalBaseWidth = finalBoundingWidth / cropWidthFactor
              const finalBaseHeight = finalBoundingHeight / cropHeightFactor

              if (
                finalBaseWidth < MIN_VIEW_WIDTH ||
                finalBaseHeight < MIN_VIEW_HEIGHT
              ) {
                return
              }

              updateImageDimensionsHandler(
                {
                  width: finalBaseWidth,
                  height: finalBaseHeight,
                },
                {
                  x: (position?.x || 0) + delta.x,
                  y: (position?.y || 0) + delta.y,
                },
              )
                .then(() => {
                  selectElement({
                    documentPageId,
                    id: template?.id as string,
                    isRemovable: true,
                    type: dataType,
                  })
                })
                .catch((error) => {
                  console.error('Error updating image size:', error)
                })
            }}
          />
        </div>
      </div>
    </OptionsContextMenu>
  )
}

export default DraggableImage
